import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Site {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  site_type: string;
  status: string;
  description: string | null;
  site_number?: string;
  project_id?: string;
  sharepoint_main_url?: string | null;
  created_at: string;
  updated_at: string;
}

interface SiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  site?: Site | null;
  onSave: () => void;
  prefilledData?: Partial<Site & { project_id?: string }>;
}

// Enhanced Dutch geocoding with better address parsing and validation
const geocodeAddress = async (address: string): Promise<{ lat: number; lon: number } | null> => {
  try {
    console.log('Geocoding address:', address);
    
    // Normalize address for Netherlands
    const normalizedAddress = address.includes('Nederland') || address.includes('Netherlands') 
      ? address 
      : `${address}, Nederland`;
    
    // First try PDOK (Netherlands specific)
    const pdokUrl = `https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest?q=${encodeURIComponent(normalizedAddress)}&rows=1`;
    
    try {
      const pdokResponse = await fetch(pdokUrl);
      if (pdokResponse.ok) {
        const pdokData = await pdokResponse.json();
        console.log('PDOK response:', pdokData);
        
        if (pdokData.response?.docs?.length > 0) {
          const doc = pdokData.response.docs[0];
          if (doc.centroide_ll) {
            const [lon, lat] = doc.centroide_ll.split(',').map(Number);
            
            // Validate coordinates are in Netherlands
            if (lat >= 50.7503 && lat <= 53.5546 && lon >= 3.3588 && lon <= 7.2275) {
              console.log('PDOK coordinates:', { lat, lon });
              return { lat, lon };
            }
          }
        }
      }
    } catch (pdokError) {
      console.warn('PDOK failed, trying Nominatim:', pdokError);
    }

    // Fallback to Nominatim
    const nominatimUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(normalizedAddress)}&format=json&limit=1&countrycodes=nl`;
    
    const nominatimResponse = await fetch(nominatimUrl);
    if (nominatimResponse.ok) {
      const nominatimData = await nominatimResponse.json();
      console.log('Nominatim response:', nominatimData);
      
      if (nominatimData.length > 0) {
        const result = nominatimData[0];
        const lat = parseFloat(result.lat);
        const lon = parseFloat(result.lon);
        
        // Validate coordinates are in Netherlands
        if (lat >= 50.7503 && lat <= 53.5546 && lon >= 3.3588 && lon <= 7.2275) {
          console.log('Nominatim coordinates:', { lat, lon });
          return { lat, lon };
        }
      }
    }

    console.warn('No valid Netherlands geocoding results found for address:', address);
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

export const SiteModal: React.FC<SiteModalProps> = ({ isOpen, onClose, site, onSave, prefilledData }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    site_type: 'office',
    status: 'active',
    description: '',
    project_id: '',
    site_number: '',
    sharepoint_main_url: ''
  });

  useEffect(() => {
    if (site) {
      setFormData({
        name: site.name || '',
        address: site.address || '',
        latitude: site.latitude?.toString() || '',
        longitude: site.longitude?.toString() || '',
        site_type: site.site_type || 'office',
        status: site.status || 'active',
        description: site.description || '',
        project_id: (site as any).project_id || '',
        site_number: site.site_number || '',
        sharepoint_main_url: site.sharepoint_main_url || ''
      });
    } else if (prefilledData) {
      setFormData({
        name: prefilledData.name || '',
        address: prefilledData.address || '',
        latitude: prefilledData.latitude?.toString() || '',
        longitude: prefilledData.longitude?.toString() || '',
        site_type: prefilledData.site_type || 'office',
        status: prefilledData.status || 'active',
        description: prefilledData.description || '',
        project_id: prefilledData.project_id || '',
        site_number: '',
        sharepoint_main_url: ''
      });
    } else {
      setFormData({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        site_type: 'office',
        status: 'active',
        description: '',
        project_id: '',
        site_number: '',
        sharepoint_main_url: ''
      });
    }
  }, [site, prefilledData]);

  const handleGeocode = async () => {
    if (!formData.address.trim()) {
      toast({
        title: 'Adres vereist',
        description: 'Voer eerst een adres in om coördinaten op te zoeken.',
        variant: 'destructive'
      });
      return;
    }

    setGeocoding(true);
    console.log('[SITE] Geocoding address:', formData.address);
    try {
      const coordinates = await geocodeAddress(formData.address);
      console.log('[SITE] Geocoding result:', coordinates);
      
      if (coordinates) {
        setFormData(prev => ({
          ...prev,
          latitude: coordinates.lat.toString(),
          longitude: coordinates.lon.toString()
        }));
        
        console.log('[SITE] Coordinates set:', coordinates);
        toast({
          title: 'Coördinaten gevonden',
          description: 'De coördinaten zijn succesvol opgehaald voor het adres.',
        });
      } else {
        console.warn('[SITE] No coordinates found for address:', formData.address);
        toast({
          title: 'Geen coördinaten gevonden',
          description: 'Kon geen coördinaten vinden voor dit adres. Voer ze handmatig in.',
          variant: 'destructive'
        });
      }
    } catch (error: any) {
      console.error('[SITE] Geocoding error:', error);
      toast({
        title: 'Fout bij ophalen coördinaten',
        description: `Er ging iets mis bij het ophalen van de coördinaten. Details: ${error.message}`,
        variant: 'destructive'
      });
    }
    setGeocoding(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      console.error('[SITE] No user found');
      toast({
        title: 'Fout',
        description: 'Je moet ingelogd zijn om een site op te slaan.',
        variant: 'destructive'
      });
      return;
    }

    // Validate coordinates
    const lat = parseFloat(formData.latitude);
    const lon = parseFloat(formData.longitude);
    
    if (!formData.latitude || !formData.longitude || !isFinite(lat) || !isFinite(lon)) {
      console.error('[SITE] Invalid coordinates:', { latitude: formData.latitude, longitude: formData.longitude });
      toast({
        title: 'Coördinaten vereist',
        description: 'Geldige latitude en longitude zijn vereist. Gebruik de "Zoek coördinaten" knop.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    console.log('[SITE] Submitting site data:', { formData, isEdit: !!site });
    
    try {
      // Validate site number uniqueness
      if (formData.site_number) {
        const { data: existingSite, error: checkError } = await supabase
          .from('sites')
          .select('id')
          .eq('site_number', formData.site_number)
          .neq('id', site?.id || '')
          .maybeSingle();

        if (checkError && checkError.code !== 'PGRST116') {
          throw new Error('Fout bij controleren sitenummer');
        }

        if (existingSite) {
          toast({
            title: 'Sitenummer bestaat al',
            description: 'Dit sitenummer is al in gebruik. Kies een ander nummer.',
            variant: 'destructive'
          });
          setLoading(false);
          return;
        }
      }

      const siteData = {
        name: formData.name,
        address: formData.address,
        latitude: lat,
        longitude: lon,
        site_type: formData.site_type,
        status: formData.status,
        description: formData.description || null,
        project_id: formData.project_id || null,
        site_number: formData.site_number || null,
        sharepoint_main_url: formData.sharepoint_main_url || null
      };

      console.log('[SITE] Processed site data:', siteData);

      if (site) {
        const { data, error } = await supabase
          .from('sites')
          .update(siteData)
          .eq('id', site.id)
          .select();

        if (error) throw error;

        console.log('[SITE] Update response:', data);
        toast({
          title: 'Site bijgewerkt',
          description: 'De site is succesvol bijgewerkt.',
        });
      } else {
        const { data, error } = await supabase
          .from('sites')
          .insert(siteData)
          .select();

        if (error) throw error;

        console.log('[SITE] Insert response:', data);
        toast({
          title: 'Site toegevoegd',
          description: 'De site is succesvol toegevoegd.',
        });
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('[SITE] Error saving site:', error);
      toast({
        title: 'Fout bij opslaan',
        description: `Er ging iets mis bij het opslaan van de site. Details: ${error.message}`,
        variant: 'destructive'
      });
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto mx-4 w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>{site ? 'Site bewerken' : 'Nieuwe site'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="site_number">Sitenummer</Label>
            <Input
              id="site_number"
              value={formData.site_number}
              onChange={(e) => setFormData(prev => ({ ...prev, site_number: e.target.value }))}
              placeholder="b.v. S2025001"
            />
          </div>
          
          <div>
            <Label htmlFor="name">Naam *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="address">Adres *</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                required
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleGeocode}
                disabled={geocoding || !formData.address.trim()}
                className="w-full sm:w-auto whitespace-nowrap"
              >
                {geocoding ? 'Zoeken...' : 'Zoek coördinaten'}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="latitude">Latitude *</Label>
              <Input
                id="latitude"
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                required
              />
            </div>
            <div>
              <Label htmlFor="longitude">Longitude *</Label>
              <Input
                id="longitude"
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="site_type">Site type</Label>
              <Select value={formData.site_type} onValueChange={(value) => setFormData(prev => ({ ...prev, site_type: value }))}>
                <SelectTrigger className="z-[10020]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10020]">
                  <SelectItem value="office">Kantoor</SelectItem>
                  <SelectItem value="warehouse">Magazijn</SelectItem>
                  <SelectItem value="factory">Fabriek</SelectItem>
                  <SelectItem value="retail">Winkel</SelectItem>
                  <SelectItem value="batterijhub">Batterijhub</SelectItem>
                  <SelectItem value="laadplein">Laadplein</SelectItem>
                  <SelectItem value="vakantiepark">Vakantiepark</SelectItem>
                  <SelectItem value="other">Anders</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger className="z-[10020]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[10020]">
                  <SelectItem value="active">Actief</SelectItem>
                  <SelectItem value="inactive">Inactief</SelectItem>
                  <SelectItem value="maintenance">Onderhoud</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="sharepoint_url">SharePoint URL</Label>
            <Input
              id="sharepoint_url"
              type="url"
              placeholder="https://yourdomain.sharepoint.com/..."
              value={formData.sharepoint_main_url}
              onChange={(e) => setFormData(prev => ({ ...prev, sharepoint_main_url: e.target.value }))}
            />
          </div>

          <div>
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Annuleren
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? 'Opslaan...' : site ? 'Bijwerken' : 'Toevoegen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
